import { useQuery } from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import { getSupabase } from "../utils";

export interface ClientReportFilters {
  search?: string;
  status?: string;
  isActive?: boolean;
}

interface ClientContactRow {
  id: string;
  name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  is_active: boolean | null;
  created_at: string;
  updated_at: string;
}

interface ClientTicketRow {
  id: string;
  status: string;
  contact_id: string;
  actual_hours: number | null;
  total_cost: number | null;
}

interface ClientTimeEntryRow {
  id: string;
  hours: number | null;
  billable_rate: number | null;
  entry_type: string | null;
  ticket:
    | { id: string; contact_id: string | null }
    | Array<{ id: string; contact_id: string | null }>
    | null;
}

function unwrapTicket(
  ticket: ClientTimeEntryRow["ticket"]
): { id: string; contact_id: string | null } | null {
  if (!ticket) return null;
  return Array.isArray(ticket) ? (ticket[0] ?? null) : ticket;
}

export function useClientReportDataWithMetrics(
  filters: ClientReportFilters,
  isClientUser: boolean,
  clientId?: string
) {
  return useQuery({
    queryKey: ["client-reports-with-metrics", filters, isClientUser, clientId],
    queryFn: async () => {
      const supabase = getSupabase();
      let contactsQuery = supabase.from("contacts").select(`
                    id,
                    name,
                    company_name,
                    email,
                    phone,
                    status,
                    is_active,
                    created_at,
                    updated_at
                `);

      if (filters.search) {
        contactsQuery = contactsQuery.or(
          `name.ilike.%${filters.search}%,company_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
        );
      }
      if (filters.status) contactsQuery = contactsQuery.eq("status", filters.status);
      if (filters.isActive !== undefined) {
        contactsQuery = contactsQuery.eq("is_active", filters.isActive);
      }
      if (isClientUser && clientId) contactsQuery = contactsQuery.eq("id", clientId);

      const { data: contactsData, error: contactsError } = await contactsQuery;

      if (contactsError) {
        logger.error("Error fetching contacts", contactsError);
        throw new Error("Failed to fetch contacts");
      }

      const contacts = (contactsData || []) as ClientContactRow[];
      if (contacts.length === 0) {
        return { data: [], total: 0 };
      }

      const contactIds = contacts.map((c) => c.id);
      const { data: ticketData, error: ticketError } = await supabase
        .from("tickets")
        .select(
          `
                    id,
                    status,
                    contact_id,
                    actual_hours,
                    total_cost
                `
        )
        .in("contact_id", contactIds);

      if (ticketError) {
        logger.error("Error fetching ticket metrics", ticketError);
        throw new Error("Failed to fetch ticket metrics");
      }

      const tickets = (ticketData || []) as ClientTicketRow[];

      const { data: timeRows, error: timeError } = await supabase
        .from("time_entries")
        .select(
          `
                    id,
                    hours,
                    billable_rate,
                    entry_type,
                    ticket:tickets!inner(id, contact_id)
                `
        )
        .in("ticket.contact_id", contactIds);

      if (timeError) {
        logger.error("Error fetching time metrics", timeError);
        throw new Error("Failed to fetch time metrics");
      }

      const timeEntries = (timeRows || []) as ClientTimeEntryRow[];

      const contactsWithMetrics = contacts.map((contact) => {
        const contactTickets = tickets.filter((t) => t.contact_id === contact.id);
        const totalTickets = contactTickets.length;
        const openTickets = contactTickets.filter(
          (t) => !["resolved", "closed", "cancelled"].includes(t.status)
        ).length;
        const totalTicketHours = contactTickets.reduce(
          (sum, t) => sum + (t.actual_hours || 0),
          0
        );
        const totalTicketCost = contactTickets.reduce(
          (sum, t) => sum + (t.total_cost || 0),
          0
        );
        const contactTimeEntries = timeEntries.filter((te) => {
          const ticket = unwrapTicket(te.ticket);
          return ticket?.contact_id === contact.id;
        });
        const totalTimeHours = contactTimeEntries.reduce(
          (sum, te) => sum + (te.hours || 0),
          0
        );
        const totalTimeRevenue = contactTimeEntries
          .filter(
            (te) => te.entry_type === "billable" && te.billable_rate && te.hours
          )
          .reduce(
            (sum, te) => sum + (te.billable_rate ?? 0) * (te.hours ?? 0),
            0
          );
        const totalRevenue = Math.max(totalTicketCost, totalTimeRevenue);
        const totalHours = Math.max(totalTicketHours, totalTimeHours);

        return {
          ...contact,
          id: contact.id,
          client_name: contact.name,
          email: contact.email,
          total_tickets: totalTickets,
          open_tickets: openTickets,
          total_hours: totalHours,
          total_revenue: totalRevenue,
          status: contact.status,
          created_date: contact.created_at,
        };
      });

      return { data: contactsWithMetrics, total: contactsWithMetrics.length };
    },
    enabled: true,
  });
}
